import { GetStaticPaths, GetStaticProps } from "next";
import { getAllWebinars, getAllWebinarsBySlug } from "../../lib/api";
import Layout from "../../components/layout";
import Link from "../../components/Link";
import { mwImage } from "../../lib/common";
import PostBody from "../../components/post-body";
import CallToActionSmall from "../../components/v2/call-to-action-sm";
import RelatedWebinars from "../../components/v2/blog/related-webinars";
import { LinkedInIcon, TwitterIcon } from "../../components/v2/icons";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import nextI18NextConfig from "../../next-i18next.config";
import "../../styles/scss/blog.module.scss";
import parse from "html-react-parser";

dayjs.extend(customParseFormat);

const parentPage = "webinar";

export default function WebinarSlug({ pageData, allWebinars }) {
  const post = pageData?.webinars?.edges?.[0]?.node;
  const featuredImage = mwImage(post?.featuredImage?.node?.sourceUrl);

  const postContent = (post?.content || "").replaceAll(
    "https://middleware.io/backend/wp-content",
    "https://middleware.io/wp-content",
  );

  const slugShare = "https://middleware.io/webinar/" + post?.slug + "/";
  const twitterShare =
    "https://x.com/share?url=" +
    encodeURIComponent(slugShare) +
    "&text=" +
    encodeURIComponent(post?.title) +
    "&via=Middleware_Labs";
  const linkedInShare =
    "https://www.linkedin.com/shareArticle?mini=true&url=" +
    encodeURIComponent(slugShare);

  const webinarDate = post?.acf_webinars?.dateTime || post?.date;
  const webinarDateString =
    typeof webinarDate === "string" ? webinarDate.trim() : "";
  const webinarLink = post?.acf_webinars?.webinarlink?.url ?? '';

  const parsedWebinarDate =
    [
      dayjs(webinarDateString),
      dayjs(webinarDateString, "YYYY-MM-DD HH:mm:ss", true),
      dayjs(webinarDateString, "YYYY-MM-DDTHH:mm:ss", true),
      dayjs(webinarDateString, "YYYY-MM-DDTHH:mm", true),
      dayjs(webinarDateString, "DD/MM/YYYY hh:mm a", true),
      dayjs(webinarDateString, "DD/MM/YYYY h:mm a", true),
      dayjs(webinarDateString, "DD/MM/YYYY hh:mm A", true),
      dayjs(webinarDateString, "MM/DD/YYYY hh:mm A", true),
      dayjs(webinarDateString.replace(" ", "T")),
    ].find((value) => value.isValid()) || null;
  const formattedWebinarDate = parsedWebinarDate
    ? parsedWebinarDate.format("D MMM YYYY")
    : "";
  const formattedWebinarTime = parsedWebinarDate
    ? parsedWebinarDate.format("h:mm A")
    : "";
  const rawSpeakers = post?.acf_webinars?.speakers;
  const speakers = Array.isArray(rawSpeakers)
    ? rawSpeakers
    : rawSpeakers && typeof rawSpeakers === "object"
      ? [rawSpeakers]
      : [];
  let validSpeakers = speakers.filter(
    (speaker) =>
      speaker &&
      (speaker?.speakerName ||
        speaker?.speakersProfile?.sourceUrl ||
        speaker?.speakersProfile?.altText),
  );

  const hostDetails = speakers.find(
    (speaker) =>
      speaker &&
      (speaker?.hostedBy ||
        speaker?.hostedByImage?.sourceUrl ||
        speaker?.hostedByLink),
  );
  const tags = Array.isArray(post?.tags?.edges)
    ? post.tags.edges.map((tag) => tag?.node?.name).filter(Boolean)
    : [];

  const registerFormEnabled =
    post?.acf_webinars?.isRegisterFormEnable === true ||
    post?.acf_webinars?.isRegisterFormEnable === "true";

  return (
    <Layout
      isHomePage={false}
      pageTitle={post?.title}
      ogImage={
        post?.featuredImage?.node?.sourceUrl ||
        "/images/middleware-webinar.webp"
      }
      uri={post?.uri}
      metaTitle={post?.seo?.title}
      metaDesc={post?.seo?.metaDesc}
      metaRobotsNoindex={post?.seo?.metaRobotsNoindex}
      metaRobotsNofollow={post?.seo?.metaRobotsNofollow}
    >
      <div className={"middle-wrapper site-page product-update-single-page"}>
        <section className={"single-blog-v2"}>
          <div className={"container"}>
            <div className={"inner-blog"}>
              <div className={"d-flex blog-main-content webinar-main-content"}>
                <div className={"main-content"}>
                  <div className="webinar-hero-section">
                    <Link href={"/webinar/"} className={"back-to-blog"}>
                      Back to Webinar
                    </Link>

                    <h1>{post?.title}</h1>

                    <div className={"category-episode"}>
                      {tags.map((tag, index) => (
                        <div
                          className={`category ${index === 0 ? "gradient-category" : "gray"}`}
                          key={`${post?.slug}-tag-${tag}-${index}`}
                        >
                          {tag}
                        </div>
                      ))}
                    </div>

                    {post?.excerpt ? parse(post.excerpt) : null}

                    <div className="blog-single-hero-image">
                      <div className={"show-play-btn"}>
                        <img
                          src={featuredImage}
                          alt={
                            post?.featuredImage?.node?.altText || post?.title
                          }
                          width="500"
                          height="255"
                          loading="lazy"
                        />

                        {post?.acf_webinars?.liveWebinarUrl && (
                          <div className={`overlay`}>
                            <a
                              target="_blank"
                              href={post.acf_webinars.liveWebinarUrl}
                              className="glass-play"
                              type="button"
                            >
                              <span>▶</span>
                              <span>Play Now</span>
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <PostBody
                    content={postContent?.replace(
                      /<iframe([^>]*)>(.*?)<\/iframe>/gi,
                      "<iframe$1></iframe>",
                    )}
                  />
                </div>
                <div className={"left-block"}>
                  <div className={"sticky-share podcast-share webinar-share"}>
                    <div className="webinar-share-content">
                      {registerFormEnabled ? (
                        <h3>
                          Register for{" "}
                          <span className="text-gradient">Webinar</span>
                        </h3>
                      ) : null}
                      <div className="date-details">
                        <p>
                          Date: <span>{formattedWebinarDate || "-"}</span>
                        </p>
                        <p>
                          Time: <span>{formattedWebinarTime || "-"}</span>
                        </p>
                        <p>
                          Location:{" "}
                          <span>{post?.acf_webinars?.location || "-"}</span>
                        </p>
                      </div>

                      <div className="author-list">
                        <div className={"left-guest"}>
                          <h4>Speaker</h4>
                          {validSpeakers.map((speaker, index) => (<>
                            <div
                              key={`${speaker?.speakerName}-${index}`}
                              className={"flex-guest"}
                            >
                              <img
                                src={
                                  speaker?.speakersProfile?.sourceUrl ||
                                  "/images/podcast/default.svg"
                                }
                                alt={
                                  speaker?.speakersProfile?.altText || "Speaker"
                                }
                                width={100}
                                height={100}
                              />
                              <div>
                                <p>{speaker?.speakerName}</p>
                                <p className="speaker-designation">{speaker?.speakerdesignation}</p>
                              </div>
                            </div>

                            {speaker?.speakerName2nd && (
                              <div
                                key={`${speaker?.speakerName2nd}-${index}`}
                                className={"flex-guest"}
                              >
                                <img
                                  src={
                                    speaker?.speakerProfile2nd?.sourceUrl ||
                                    "/images/podcast/default.svg"
                                  }
                                  alt={
                                    speaker?.speakerProfile2nd?.altText || "Speaker"
                                  }
                                  width={100}
                                  height={100}
                                />
                                <div>
                                  <p>{speaker?.speakerName2nd}</p>
                                  <p className="speaker-designation">{speaker?.speakerDesignation2nd}</p>
                                </div>
                              </div>)}
                          </>

                          ))}
                        </div>

                        {hostDetails ? (
                          <div className={"left-guest"}>
                            <h4>Host</h4>
                            <div className={"flex-guest"}>
                              <img
                                src={
                                  hostDetails?.hostedByImage?.sourceUrl ||
                                  "/images/podcast/default.svg"
                                }
                                alt={
                                  hostDetails?.hostedByImage?.altText || "Host"
                                }
                                width={100}
                                height={100}
                              />
                              <div>
                                {hostDetails?.hostedByLink ? (
                                  <Link
                                    href={hostDetails?.hostedByLink}
                                    target={"_blank"}
                                    skipLocaleHandling={true}
                                  >
                                    <p>{hostDetails?.hostedBy}</p>
                                  </Link>
                                ) : (
                                  <p>{hostDetails?.hostedBy}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <RelatedWebinars selectedWebinar={post} data={allWebinars} />

        <CallToActionSmall />
      </div>
    </Layout>
  );
}

export const getStaticProps: GetStaticProps = async (ctx) => {
  const { params } = ctx;
  const data = await getAllWebinarsBySlug(params?.slug as string, parentPage);
  const post = data?.webinars?.edges?.[0]?.node;

  if (!post) {
    return { notFound: true };
  }

  const allWebinars = await getAllWebinars();

  const translations = await serverSideTranslations(
    ctx?.locale ?? "en",
    ["common", "header", "footer"],
    nextI18NextConfig,
  );
  return {
    props: {
      ...translations,
      pageData: data,
      allWebinars,
      pageTitle: post?.seo?.title,
      description: post?.seo?.metaDesc,
      uri: post?.uri,
      metaTitle: post?.seo?.title,
      metaDesc: post?.seo?.metaDesc,
      metaRobotsNoindex: post?.seo?.metaRobotsNoindex,
      metaRobotsNofollow: post?.seo?.metaRobotsNofollow,
    },
  };
};

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  const webinars = await getAllWebinars();
  const paths =
    webinars?.edges?.flatMap(({ node }) => {
      return (locales || nextI18NextConfig.i18n.locales).map((locale) => ({
        params: { slug: node?.slug },
        locale,
      }));
    }) || [];
  return {
    paths,
    fallback: "blocking",
  };
};
